import {
  createContext,
  useContext,
  useMemo,
  useSyncExternalStore,
  type ReactNode,
} from 'react'
import { createAnnotationStore, type LocalAnnotationStore } from './annotations.ts'
import { createFriendStore, type LocalFriendStore } from './friends.ts'
import type { Annotation, Friend, SharedAnnotation, StorageMode, Vote } from './types.ts'

const StoreContext = createContext<LocalAnnotationStore | null>(null)
const FriendStoreContext = createContext<LocalFriendStore | null>(null)

export function StoreProvider({
  children,
  store,
  friends,
}: {
  children: ReactNode
  /** Injectable for tests; defaults to a localStorage-backed store. */
  store?: LocalAnnotationStore
  /**
   * The friend list, injectable the same way.
   *
   * A prop rather than a module singleton because a component test has to be able to
   * hand a component a specific list — the name prefix on a shared note is decided by
   * what is in here, so a test of that rendering has nowhere else to say it.
   */
  friends?: LocalFriendStore
}) {
  const value = useMemo(() => store ?? createAnnotationStore(), [store])
  const friendValue = useMemo(() => friends ?? createFriendStore(), [friends])
  return (
    <StoreContext.Provider value={value}>
      <FriendStoreContext.Provider value={friendValue}>{children}</FriendStoreContext.Provider>
    </StoreContext.Provider>
  )
}

export function useStore(): LocalAnnotationStore {
  const store = useContext(StoreContext)
  if (!store) throw new Error('useStore må brukes innenfor StoreProvider')
  return store
}

export function useFriendStore(): LocalFriendStore {
  const store = useContext(FriendStoreContext)
  if (!store) throw new Error('useFriendStore må brukes innenfor StoreProvider')
  return store
}

/**
 * Subscribes to the store's write counter.
 *
 * The counter, not the data, is the snapshot: a number is referentially stable,
 * while returning a freshly-built array would make `useSyncExternalStore` re-render
 * forever. Any write re-renders every subscriber, which is the right trade at 88
 * concerts.
 */
function useStoreVersion(): number {
  const store = useStore()
  return useSyncExternalStore(store.subscribe, store.getVersion, store.getVersion)
}

/** The same binding for the friend list, whose version counter is its own. */
function useFriendStoreVersion(): number {
  const store = useFriendStore()
  return useSyncExternalStore(store.subscribe, store.getVersion, store.getVersion)
}

/**
 * Everyone in the friend list: accepted friends and requests both ways.
 *
 * Deliberately the whole list. A grant — the `readers` array on a write — must come
 * from `acceptedUids()` on the store instead, which is the only source that cannot
 * name someone who has not agreed yet (KTD4).
 */
export function useFriends(): Friend[] {
  const store = useFriendStore()
  useFriendStoreVersion()
  return store.friends()
}

/**
 * How many friend requests are waiting on this user, for the header badge (R31).
 *
 * Reads the store's own count rather than re-deriving it at the call site, so there is
 * exactly one answer to "what counts as incoming" — a header that filtered the list
 * itself would agree only by construction, which is the kind of agreement that quietly
 * stops holding.
 */
export function useIncomingCount(): number {
  const store = useFriendStore()
  useFriendStoreVersion()
  return store.incomingCount()
}

export function useAnnotation(concertId: string): Annotation | null {
  const store = useStore()
  useStoreVersion()
  return store.getAnnotation(concertId)
}

export function useAllAnnotations(): Annotation[] {
  const store = useStore()
  useStoreVersion()
  return store.allAnnotations()
}

export function useSharedAnnotations(concertId: string): SharedAnnotation[] {
  const store = useStore()
  useStoreVersion()
  return store.sharedAnnotations(concertId)
}

export function useStorageMode(): StorageMode {
  const store = useStore()
  useStoreVersion()
  return store.storageMode
}

export function useVoteOf(concertId: string): Vote {
  return useAnnotation(concertId)?.vote ?? null
}
